<template>
	<div :style="{ height: height + 'px' }">
		<vue-quill-editor v-bind="$attrs"
		    v-on="$listeners"
		    ref="quillEditor"
		    :style="{ height: height - 50 + 'px' }"
		    :value="value"
		    :options="options"
		    :disabled="readonly || disabled"
		    @input="onInput">
		</vue-quill-editor>
		<!-- 图片上传隐藏域 -->
		<Upload v-show="false"
		    ref="uploader"
		    name="files"
		    accept="image/*"
		    multiple
		    with-credentials
		    :show-upload-list="false"
		    :format="imgSuffix"
		    :max-size="imgMaxSize"
		    :disabled="readonly || disabled"
		    :action="baseURL + action"
		    :headers="httpHeaders"
		    :on-success="handleSuccess"
		    :on-format-error="handleFormatError"
		    :on-exceeded-size="handleMaxSize">
		</Upload>
	</div>
</template>
<script>
import { Field } from "ivux"
import { quillEditor } from 'vue-quill-editor'
import { getAbsStaticPath, getCustomHeaders } from '_u/helper'
import config from '@/config'
import { fileDelete, fileUpload } from '@/api/sys-oss'

export default {
	nane: "QuillEditor",

	mixins: [Field],

	components: {
		VueQuillEditor: quillEditor,
	},

	props: {
		value: {
			type: String,
			default: '',
		},
		placeholder: {
			type: String,
			default: "",
		},
		toolbar: {
			type: Array,
			default: () => [
				['bold', 'italic', 'underline', 'strike'], //加粗，斜体，下划线，删除线
				['blockquote', 'code-block'], //引用，代码块
				// [{ 'header': 1 }, { 'header': 2 }],        // 标题，键值对的形式；1、2表示字体大小
				[{ list: 'ordered' }, { list: 'bullet' }], //列表
				// [{ 'script': 'sub'}, { 'script': 'super' }],   // 上下标
				[{ indent: '-1' }, { indent: '+1' }], // 缩进
				// [{ 'direction': 'rtl' }],             // 文本方向
				// [{ 'size': ['small', false, 'large', 'huge'] }], // 字体大小
				[{ header: [1, 2, 3, 4, 5, 6, false] }], //几级标题
				[{ color: [] }, { background: [] }], // 字体颜色，字体背景颜色
				// [{ 'font': [] }],     //字体
				[{ align: [] }], //对齐方式
				// ['clean'],      //清除字体样式
				['image'] //图片
			]
		},
		theme: {
			type: String,
			default: "snow",
		},
		height: {
			type: Number,
			default: 260
		},
		action: {
			type: String,
			default: config.upload.path
		},
		// 图片大小限制
		imgMaxSize: {
			type: Number,
			default: 10240, // kb, 默认10M
		},
		// 图片存储是否使用base64
		base64: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			imgSuffix: config.upload.imgSuffix,
			baseURL: config.baseURL,
			uploader: null,
		}
	},

	computed: {
		options() {
			return {
				placeholder: this.placeholder,
				modules: {
					toolbar: this.toolbar,
				},
				theme: this.theme,
			}
		},

		httpHeaders() {
            return getCustomHeaders()
		},
	},

	mounted() {
		this.quill = this.$refs.quillEditor.quill
		this.uploader = this.$refs.uploader
		// 自定义工具栏图片上传，参考 https://github.com/quilljs/quill/issues/1400#issuecomment-318260547
		if (this.base64 == false) {
			this.quill.getModule('toolbar').addHandler('image', this.handleUpload)
		}

		// 自定义拖拽/粘贴图片功能，参考 https://github.com/kensnyder/quill-image-drop-module
		this.quill.root.addEventListener('drop', this.handleDrop, false)
		this.quill.root.addEventListener('paste', this.handlePaste, false)
	},

	methods: {
		onInput(val) {
			this.$emit("input", val)
		},

		handleUpload(value) {
			// 若点击图片，则value值为true
			if (value) {
				// 触发控件上传
				this.uploader.handleClick()
			}
		},

		handleSuccess(res, file, fileList) {
			if (res.code == '200') {
				var data = res.data.list[0]

				// img url
				this.insert(getAbsStaticPath(data.path))
			} else {
				// 通知
				this.$Notice.warning({
					title: '图片上传失败',
					desc: res.msg
				})
			}
		},

		handleFormatError(file) {
			this.$Notice.warning({
				title: '图片上传失败',
				desc: `${file.name}文件格式不正确（只支持${this.format.join(
                    '、'
                )}）`
			})
		},

		handleMaxSize(file) {
			this.$Notice.warning({
				title: '图片上传失败',
				desc: `${file.name}体积太大（不能超过${this.maxSize / 1024}M）`
			})
		},

		handleDrop(e) {
			if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) {
				// 拦截文件拖放
				e.preventDefault()
				// 目前只处理图片拖放
				this.handleImgBlob(e.dataTransfer.files)
			}
		},

		handlePaste(e) {
			if (e.clipboardData && e.clipboardData.files && e.clipboardData.files.length) {
				// 拦截文件粘贴
				e.preventDefault()
				// 目前只处理粘贴图片
				this.handleImgBlob(e.clipboardData.files)
			}
		},

		handleImgBlob(files) {
			var validFiles = [];

			[...files].forEach(file => {
				if (!file.type.match(/^image\/(gif|jpe?g|a?png|svg|webp|bmp|vnd\.microsoft\.icon)/i)) {
					// file is not an image
					// Note that some file formats such as psd start with image/* but are not readable

					this.handleFormatError(file)
				} else {
					validFiles.push(file)
				}
			})

			if (validFiles.length) {
				if (this.base64) {
					this.convertFilesToDataUrl(validFiles).then(args => {
						args.forEach(v => this.insert(v))
					})
				} else {
					this.uploadFiles(validFiles).then(args => {
						args.forEach(v => this.handleSuccess(v))
					}, err => this.$Notice.warning({
						title: '图片上传失败',
						desc: err.msg
					}))
				}
			}
		},

		convertFilesToDataUrl(files) {
			var result = []

			return new Promise((resolve, reject) => {
				// check each file for an image
				[].forEach.call(files, (file, index) => {
					// set up file reader
					const reader = new FileReader()
					reader.onload = (e) => {
						result[index] = e.target.result

						if(result.length == files.length){
							resolve(result)
						}
					}

					// read the clipboard item or file
					const blob = file.getAsFile ? file.getAsFile() : file

					if (blob instanceof Blob) {
						reader.readAsDataURL(blob)
					}
				})
			})
		},

		// 向编辑器插入图片
		insert(imgSrc) {
			// img position
			var currentPosition = this.quill.getSelection()
			// 插入编辑框
			this.quill.insertEmbed(currentPosition.index, 'image', imgSrc, "user")
			// 调整光标到图片后
			this.quill.setSelection(currentPosition.index + 1)
		},

		// 上传文件
		uploadFiles(files) {
			var files = [...files]

			return Promise.all(files.map(file => {
				var formData = new FormData()

				formData.append('files', file)

				return fileUpload(formData)
			}))
		},
	},
}
</script>
